import { submitServerChange } from 'components/dataCreationForms/common';
import { OpenCreateDialogParams } from 'components/dataCreationForms/types';
import { useCancelConfirmation } from 'presentation/dialogForms/dialogFormsHooks';
import {
  ComponentType,
  ReactElement,
  createContext,
  useCallback,
  useContext,
  useMemo,
  useState,
} from 'react';
import {
  useCurrentUserInProjectLabelled,
  useCurrentUserInProjectModel,
} from 'store/user';
import { useProjectId, useProjectTimezone } from 'store/project';
import { getLevelsStoreState } from 'store/level';
import { getProjectUsersStoreState } from 'store/projectUsers';
import { getSitesStoreState } from 'store/site';
import { siteOnViewToSiteCreateModel } from 'shared/domain/site/mapping/toModel';
import { siteClient } from 'shared/domain/site/siteClient';
import { SiteOnView } from 'shared/domain/site/types/view';
import { toOptionalUserWithPersonalData } from 'shared/domain/user/mapping/toView';
import { nowTimezonedDateTime } from 'shared/utils/date/dates';
import { createEmptySite } from 'views/site/model';
import { CreateSiteContext } from '../types';
import { handleFormValidation, validate } from '../validation';
const formName = 'site';
const SUBMIT_EVENT_TYPE = 'submit-site';
const WithCreateSiteContext = createContext<CreateSiteContext | undefined>(
  undefined
);

function WithCreateSite({
  children,
  onSuccessCallback,
}: {
  children: ReactElement;
  onSuccessCallback?: (_id: string, label: string) => void;
}): ReactElement {
  const [isPosting, setIsPosting] = useState<boolean>(false);
  const projectId = useProjectId();
  const releaseSubmitEvent = useCallback(() => {
    window.dispatchEvent(new CustomEvent(SUBMIT_EVENT_TYPE));
  }, []);

  const currentUserLabelled = useCurrentUserInProjectLabelled();
  const currentUserModel = useCurrentUserInProjectModel();

  const timezone = useProjectTimezone();

  const emptySite = createEmptySite(
    nowTimezonedDateTime(timezone),
    currentUserLabelled
  );
  const [initialValues, setInitialValues] =
    useState<SiteOnView>(emptySite);

  const [open, setOpen] = useState(false);

  const openDialog = useCallback(
    ({ freeSoloLabel }: OpenCreateDialogParams) => {
      setOpen(true);
      initialValues &&
        freeSoloLabel &&
        setInitialValues({ ...initialValues, label: freeSoloLabel });
    },
    [initialValues]
  );
  const onSuccess = useCallback(
    (_id: string, label: string) =>
      onSuccessCallback?.(_id, label),
    [onSuccessCallback]
  );
  const confirmCancel = useCancelConfirmation(formName);
  const openConfirmationDialog = useCallback(async () => {
    const { cancelConfirmed } = await confirmCancel();
    if (!cancelConfirmed) {
      return;
    }
    setOpen(false);
  }, [confirmCancel]);

  const submitForm = useCallback(
    async (values: SiteOnView) => {
      const siteCreateModel = siteOnViewToSiteCreateModel(
        values,
        projectId,
        toOptionalUserWithPersonalData(currentUserModel)
      );
      const { saved } = await submitServerChange({
        change: () => siteClient.create(siteCreateModel),
        setOpen,
        setIsPosting,
        action: 'creating a site',
      });
      if (!saved) {
        return;
      }

      // Users are attached to sites: without this nobody could be assigned before a refresh.
      void getProjectUsersStoreState().actions.reload();
      // The first level is created with the site.
      void getLevelsStoreState().actions.reload();
      const createdSite = (await getSitesStoreState().actions.reload())?.find(
        (site) => site.label === values.label && site.code === values.code
      );
      if (createdSite) {
        onSuccess(createdSite._id, createdSite.label);
      }
    },
    [currentUserModel, projectId, onSuccess]
  );

  const ctx: CreateSiteContext = useMemo(() => {
    return {
      open,
      openDialog,
      closeDialog: openConfirmationDialog,
      submitForm,
      isPosting,
      initialValues,
      SUBMIT_EVENT_TYPE,
      releaseSubmitEvent,
      validate,
      handleFormValidation,
      onSuccess,
      formName,
    };
  }, [
    openConfirmationDialog,
    initialValues,
    isPosting,
    open,
    openDialog,
    releaseSubmitEvent,
    submitForm,
    onSuccess,
  ]);

  return (
    <WithCreateSiteContext.Provider value={ctx}>
      {children}
    </WithCreateSiteContext.Provider>
  );
}

function useCreateSite(): CreateSiteContext {
  const context = useContext(WithCreateSiteContext);
  if (context === undefined) {
    throw new Error(
      'useCreateSite must be used within an CreateSiteContext'
    );
  }
  return context;
}

const withCreateSite =
  (Component: ComponentType<any>) =>
  ({ ...props }): ReactElement => (
    <WithCreateSite>
      <Component {...props} />
    </WithCreateSite>
  );

export { WithCreateSite, useCreateSite, withCreateSite };
